import React, { useLayoutEffect, useRef, useState } from 'react'

const VB_H = 49
const STROKE = '#8A8E97'
const STROKE_W = 1.15
const Y_JOIN = 26.5
const Y_SHOULDER = 41.5
const Y_TIP = 44
const CHEVRON_HALF_W = 2.85
const hw = CHEVRON_HALF_W
const JOINT_R = 9
const MIN_W = 80

const BRANCH_COLUMN_GAP_PX = 128

export type FlowBranchForkProps = {
  branches: 2 | 3
  className?: string
}

// Column centers for `repeat(N, 1fr)` with fixed `gap` between tracks
const dropXs = (branches: 2 | 3, w: number): number[] => {
  const g = BRANCH_COLUMN_GAP_PX
  if (branches === 3) {
    const c = Math.max(0, (w - 2 * g) / 3)
    return [c / 2, w / 2, w - c / 2]
  }
  const c = Math.max(0, (w - g) / 2)
  return [c / 2, w - c / 2]
}

function jointRadius(midX: number, leftX: number, rightX: number): number {
  const leftSpace = (midX - leftX) / 2 - 0.25
  const rightSpace = (rightX - midX) / 2 - 0.25
  return Math.max(1.5, Math.min(JOINT_R, leftSpace, rightSpace))
}

/** Stem down, rounded split toward −X, along bar, rounded outer bend, stem + open chevron (one stroke). */
function pathLeftArm(
  midX: number,
  leftX: number,
  r: number,
  includeTrunkFromTop: boolean,
): string {
  const start = includeTrunkFromTop
    ? `M ${midX} 0 L ${midX} ${Y_JOIN - r} Q ${midX} ${Y_JOIN} ${midX - r} ${Y_JOIN} `
    : `M ${midX} ${Y_JOIN - r} Q ${midX} ${Y_JOIN} ${midX - r} ${Y_JOIN} `
  const barToChev = `L ${leftX + r} ${Y_JOIN} Q ${leftX} ${Y_JOIN} ${leftX} ${Y_JOIN + r} L ${leftX} ${Y_SHOULDER}`
  const chev = `L ${leftX - hw} ${Y_SHOULDER} L ${leftX} ${Y_TIP} L ${leftX + hw} ${Y_SHOULDER}`
  return `${start}${barToChev} ${chev}`
}

function pathRightArm(midX: number, rightX: number, r: number): string {
  return [
    `M ${midX} ${Y_JOIN - r} Q ${midX} ${Y_JOIN} ${midX + r} ${Y_JOIN}`,
    `L ${rightX - r} ${Y_JOIN} Q ${rightX} ${Y_JOIN} ${rightX} ${Y_JOIN + r}`,
    `L ${rightX} ${Y_SHOULDER} L ${rightX - hw} ${Y_SHOULDER} L ${rightX} ${Y_TIP}`,
    `L ${rightX + hw} ${Y_SHOULDER}`,
  ].join(' ')
}

function pathCenterArm(cx: number, midX: number, r: number): string {
  return `M ${midX} ${Y_JOIN - r} L ${cx} ${Y_SHOULDER} L ${cx - hw} ${Y_SHOULDER} L ${cx} ${Y_TIP} L ${cx + hw} ${Y_SHOULDER}`
}

function forkPaths(branches: 2 | 3, w: number): string {
  const xs = dropXs(branches, w)
  const [leftX, rightX] = [xs[0]!, xs[xs.length - 1]!]
  const midX = w / 2
  const r = jointRadius(midX, leftX, rightX)

  let d = pathLeftArm(midX, leftX, r, true)
  d += pathRightArm(midX, rightX, r)

  if (branches === 3) {
    const c = xs[1]!
    d += pathCenterArm(c, midX, r)
  }

  return d
}

const FlowBranchFork = ({
  branches,
  className,
}: FlowBranchForkProps): React.ReactElement => {
  const wrapRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)

  useLayoutEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect
      if (!cr) return
      setWidth(Math.max(MIN_W, Math.floor(cr.width)))
    })
    ro.observe(el)
    setWidth(Math.max(MIN_W, Math.floor(el.getBoundingClientRect().width)))
    return () => ro.disconnect()
  }, [])

  const w = Math.max(width, MIN_W)

  return (
    <div
      ref={wrapRef}
      className={className}
      style={{ width: '100%', minWidth: 0 }}
    >
      <svg
        width="100%"
        height={VB_H}
        viewBox={`0 0 ${w} ${VB_H}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
        focusable="false"
        style={{ display: 'block', width: '100%', height: VB_H }}
      >
        <path
          d={forkPaths(branches, w)}
          stroke={STROKE}
          strokeWidth={STROKE_W}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  )
}

export default FlowBranchFork
